<script>
import ProposalByKpi from "@/components/widgets/proposal-by-kpi.vue";
import ProposalByOpcr from "@/components/widgets/proposal-by-opcr.vue";

export default {
    name: "ProposalTabs",
    components: {
        ProposalByKpi,
        ProposalByOpcr,
    },
    props: {
        du: {
            type: Object,
            required: true,
        },
        campus: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        viewFrom: {
            type: String,
            default: "planning",
        },
        filter: {
            type: String,
            default: "",
        },
        campIndex: {
            type: Number,
            default: 0,
        },
        duIndex: {
            type: Number,
            default: 0,
        },
        totalCampuses: {
            type: Number,
            default: 1,
        },
        department: {
            type: [Object, String, Boolean],
            default: null,
        },
        selectedTemplate: {
            type: Object,
            default: () => ({}),
        },
        budgets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tabIndex: 0,
            targets: [],
            loadingTargets: false,
            opcrs: [],
            loadingOpcrs: false,
        };
    },
    watch: {
        tabIndex: {
            immediate: true,
            async handler(newVal) {
                if (newVal === 0 && this.du && this.du.isOpen) {
                    await this.getBudgetProposalTargets();
                } else if (newVal === 1 && this.du && this.du.isOpen) {
                    await this.getMyOpcrs();
                }
            },
        },
        "du.isOpen": {
            immediate: true,
            async handler(newVal) {
                if (newVal) {
                    if (this.tabIndex === 0) {
                        await this.getBudgetProposalTargets();
                    } else if (this.tabIndex === 1) {
                        await this.getMyOpcrs();
                    }
                }
            },
        },
    },
    methods: {
        async getBudgetProposalTargets() {
            if (!this.du || !this.du.isOpen || !this.du.id || !this.selectedTemplate || !this.selectedTemplate.id) {
                return;
            }
            if (this.targets.length) {
                return;
            }
            this.loadingTargets = true;
            try {
                const { data: response } = await this.$store.dispatch(
                    "planningtarget/GetBudgetProposalTargets",
                    {
                        deliveryUnitId: this.du.id,
                        budgetProposalId: this.selectedTemplate.id,
                    }
                );
                if (response && response.error) {
                    if (this.showToast) {
                        this.showToast(response.message, "error");
                    }
                    this.targets = [];
                } else {
                    this.targets = Array.isArray(response) ? response : [];
                }
            } catch (error) {
                if (this.showToast) {
                    this.showToast("Failed to fetch budget proposal targets.", "error");
                }
                this.targets = [];
            } finally {
                this.loadingTargets = false;
            }
        },
        async getMyOpcrs() {
            if (!this.du || !this.du.isOpen || !this.du.id || !this.selectedTemplate || !this.selectedTemplate.id) {
                return;
            }
            if (this.opcrs.length) {
                return;
            }
            this.loadingOpcrs = true;
            try {
                const { data: response } = await this.$store.dispatch(
                    "preopcr/GetProposalPreOpcr",
                    {
                        proposalId: this.selectedTemplate.id,
                        deliveryUnitId: this.du.id,
                    }
                );
                if (response && response.error) {
                    if (this.showToast) {
                        this.showToast(response.errorMessage || response.message, "error");
                    }
                    this.opcrs = [];
                } else if (!response) {
                    this.opcrs = [];
                } else {
                    this.opcrs = Array.isArray(response) ? response : [response];
                }
            } catch (err) {
                if (this.showToast) {
                    this.showToast("Getting Opcrs Failed", "error");
                }
                this.opcrs = [];
            } finally {
                this.loadingOpcrs = false;
            }
        },
    },
};
</script>

<template>
    <div class="proposal-tabs">
        <b-tabs v-model="tabIndex" content-class="pt-2 text-muted">
            <b-tab title="By KPI" active>
                <ProposalByKpi :targets="targets" :loading="loadingTargets" :du="du" :campus="campus" :type="type"
                    :viewFrom="viewFrom" :filter="filter" :campIndex="campIndex" :duIndex="duIndex"
                    :totalCampuses="totalCampuses" :department="department" :selectedTemplate="selectedTemplate"
                    :budgets="budgets" @view-resources="$emit('view-resources', $event)"
                    @show-api-attachments="$emit('show-api-attachments', $event)"
                    @show-api-logs="$emit('show-api-logs', $event)"
                    @view-proposal-data="$emit('view-proposal-data', $event)"
                    @approve-proposal="$emit('approve-proposal', $event)"
                    @return-proposal="$emit('return-proposal', $event)"
                    @shortlist-proposal="$emit('shortlist-proposal', $event)"
                    @park-proposal="$emit('park-proposal', $event)" @add-comment="$emit('add-comment', $event)"
                    @change-expenditure="$emit('change-expenditure', $event)"
                    @view-attachments="$emit('view-attachments', $event)"
                    @assign-fund-source="$emit('assign-fund-source', { ...$event, campus: $event && $event.campus ? $event.campus : campus, campusId: $event && $event.campusId ? $event.campusId : (campus ? campus.id : null) })" />
            </b-tab>
            <b-tab title="By OPCR">
                <ProposalByOpcr :opcrs="opcrs" :loading="loadingOpcrs" :du="du" :campus="campus" :type="type"
                    :viewFrom="viewFrom" :filter="filter" :campIndex="campIndex" :duIndex="duIndex"
                    :totalCampuses="totalCampuses" :department="department" :selectedTemplate="selectedTemplate"
                    :budgets="budgets" @view-resources="$emit('view-resources', $event)"
                    @show-api-attachments="$emit('show-api-attachments', $event)"
                    @show-api-logs="$emit('show-api-logs', $event)"
                    @view-proposal-data="$emit('view-proposal-data', $event)"
                    @approve-proposal="$emit('approve-proposal', $event)"
                    @return-proposal="$emit('return-proposal', $event)"
                    @shortlist-proposal="$emit('shortlist-proposal', $event)"
                    @park-proposal="$emit('park-proposal', $event)" @add-comment="$emit('add-comment', $event)"
                    @change-expenditure="$emit('change-expenditure', $event)"
                    @view-attachments="$emit('view-attachments', $event)"
                    @assign-fund-source="$emit('assign-fund-source', { ...$event, campus: $event && $event.campus ? $event.campus : campus, campusId: $event && $event.campusId ? $event.campusId : (campus ? campus.id : null) })" />
            </b-tab>
        </b-tabs>
    </div>
</template>
